import React, { useEffect, useState } from 'react';
import { Loader2, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CampingSpot } from '../../types';
import { fetchNearestSpotAmenities, NearbySpotAmenities, NearbySpotAmenity } from '../../services/overpassService';
import NearbyAmenityRouteOverlay from './NearbyAmenityRouteOverlay';

interface SpotProximityFactsProps {
  spot: CampingSpot;
  active?: boolean;
  compact?: boolean;
}

const MAX_GROCERY_DISTANCE_M = 15000;
const MAX_PLAYGROUND_DISTANCE_M = 10000;

// Rutsjebane-ikon for lekeplass (lucide mangler et passende ikon).
const SlideIcon: React.FC<{ size?: number; className?: string }> = ({ size = 12, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 21h18" />
    <path d="M7 21V6h3" />
    <path d="M10 6c0 8-1 15 8 15" />
  </svg>
);

const formatDistance = (distanceM: number) => {
  if (distanceM < 1000) return `${Math.max(10, Math.round(distanceM / 10) * 10)} m`;
  return `${(distanceM / 1000).toFixed(distanceM < 10000 ? 1 : 0)} km`;
};

const SpotProximityFacts: React.FC<SpotProximityFactsProps> = ({ spot, active = true, compact = false }) => {
  const { t } = useTranslation();
  const [amenities, setAmenities] = useState<NearbySpotAmenities | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [overlay, setOverlay] = useState<{ amenity: NearbySpotAmenity; kind: 'grocery' | 'playground' } | null>(null);

  useEffect(() => {
    if (!active || !spot?.location) return;
    let cancelled = false;
    setAmenities(null);
    setIsLoading(true);

    fetchNearestSpotAmenities(spot.location)
      .then((result) => {
        if (!cancelled) setAmenities(result);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active, spot.id, spot.location?.latitude, spot.location?.longitude]);

  if (!active) return null;

  const grocery = amenities?.grocery && amenities.grocery.distanceM <= MAX_GROCERY_DISTANCE_M
    ? amenities.grocery
    : null;
  const playground = amenities?.playground && amenities.playground.distanceM <= MAX_PLAYGROUND_DISTANCE_M
    ? amenities.playground
    : null;

  if (!isLoading && !grocery && !playground) return null;

  const itemClass = compact
    ? 'inline-flex items-center gap-1 text-3xs font-black uppercase tracking-tighter text-[var(--text-secondary)]'
    : 'inline-flex items-center gap-1.5 text-tiny font-black uppercase text-[var(--text-muted)]';
  const iconSize = compact ? 10 : 12;

  return (
    <>
      {grocery && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOverlay({ amenity: grocery, kind: 'grocery' }); }}
          className={`${itemClass} hover:text-emerald-400 transition-colors cursor-pointer`}
          title={t('spot.proximity.grocery_title', { defaultValue: 'Nærmeste dagligvare: {{name}}', name: grocery.name || t('spot.proximity.grocery', 'Dagligvare') })}
        >
          <ShoppingCart size={iconSize} />
          {formatDistance(grocery.distanceM)}
        </button>
      )}
      {playground && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOverlay({ amenity: playground, kind: 'playground' }); }}
          className={`${itemClass} hover:text-emerald-400 transition-colors cursor-pointer`}
          title={t('spot.proximity.playground_title', { defaultValue: 'Nærmeste lekeplass: {{name}}', name: playground.name || t('spot.proximity.playground', 'Lekeplass') })}
        >
          <SlideIcon size={iconSize} />
          {formatDistance(playground.distanceM)}
        </button>
      )}
      {isLoading && !grocery && !playground && (
        <span className={itemClass} title={t('spot.proximity.loading', 'Sjekker nærliggende fasiliteter')}>
          <Loader2 size={iconSize} className="animate-spin" />
        </span>
      )}
      {overlay && (
        <NearbyAmenityRouteOverlay
          spotLocation={spot.location}
          amenity={overlay.amenity}
          kind={overlay.kind}
          onClose={() => setOverlay(null)}
        />
      )}
    </>
  );
};

export default React.memo(SpotProximityFacts);
